// Client side of subject tagging (the tag-subjects edge function).
//
// queueSubjectTagging: called by canvasStore after items are created, and
// when a link's pending preview lands. Ids collect for a few seconds and go
// out in batches, so a bulk import makes one request per 24 items, not one
// per item.
//
// requestSubjectBackfill: called once by the mobile Library on mount. At
// most once a day per device, it asks the function to tag untagged items,
// batch after batch up to the daily cap, then hands back so the caller can refetch.
//
// Everything here is fire-and-forget: tagging failing never blocks or
// interrupts saving.

import { supabase } from '@/integrations/supabase/client';
import { isAwaitingPreview, isTaggable, type SubjectItemRow } from '../../../supabase/functions/_shared/subjectInput';

export interface TaggedSubjects {
  id: string;
  subjects: string[];
}

interface TagResponse {
  tagged?: TaggedSubjects[];
  remaining?: boolean;
  capped?: boolean;
}

/** Same as the function's BATCH_SIZE. */
export const SUBJECT_BATCH_SIZE = 24;
/** Long enough for a bulk import (and the 500ms mutation queue) to settle. */
export const SUBJECT_DEBOUNCE_MS = 4000;
const BACKFILL_KEY = 'mindfull-subject-backfill-at';
const BACKFILL_INTERVAL_MS = 24 * 60 * 60 * 1000;
/** 20 x 24 = 480, the function's daily cap per person: a big library fills in over a few days, newest first. */
export const BACKFILL_MAX_BATCHES = 20;

type Listener = (results: TaggedSubjects[]) => void;

const queued = new Set<string>();
let timer: ReturnType<typeof setTimeout> | null = null;
let listener: Listener | null = null;

/** Where tagging results go (canvasStore patches its items). One listener. */
export function onSubjectsTagged(fn: Listener | null): void {
  listener = fn;
}

async function invokeTagSubjects(body: { itemIds?: string[]; backfill?: boolean }): Promise<TagResponse | null> {
  const { data, error } = await supabase.functions.invoke('tag-subjects', { body });
  if (error) {
    console.warn('[Subjects] Tagging failed:', error.message ?? error);
    return null;
  }
  const res = (data ?? {}) as TagResponse;
  if (res.tagged?.length) listener?.(res.tagged);
  return res;
}

async function flush(): Promise<void> {
  timer = null;
  const ids = [...queued];
  queued.clear();
  for (let i = 0; i < ids.length; i += SUBJECT_BATCH_SIZE) {
    const res = await invokeTagSubjects({ itemIds: ids.slice(i, i + SUBJECT_BATCH_SIZE) });
    if (!res || res.capped) break;
  }
}

/** Whether a freshly saved item should be tagged now (folders never; pending links later). */
export function shouldTagNow(item: Pick<SubjectItemRow, 'item_type' | 'content'> & { isGhost?: boolean }): boolean {
  return !item.isGhost && isTaggable(item) && !isAwaitingPreview(item);
}

/** Queue items for tagging. Items that should not be tagged now are ignored. */
export function queueSubjectTagging(items: Array<Pick<SubjectItemRow, 'id' | 'item_type' | 'content'> & { isGhost?: boolean }>): void {
  let added = false;
  for (const item of items) {
    if (!shouldTagNow(item)) continue;
    queued.add(item.id);
    added = true;
  }
  if (!added) return;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    flush().catch((e) => console.warn('[Subjects] Flush failed:', e));
  }, SUBJECT_DEBOUNCE_MS);
}

function readStamp(): number {
  try {
    return Number(localStorage.getItem(BACKFILL_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeStamp(at: number): void {
  try {
    localStorage.setItem(BACKFILL_KEY, String(at));
  } catch {
    // Private mode or blocked storage: backfill may run again next mount, which is harmless
  }
}

let backfillRunning = false;

/**
 * Tag untagged items, at most once a day per device. Resolves to how many
 * items were tagged (0 when throttled or nothing was left).
 */
export async function requestSubjectBackfill(opts: { now?: number; force?: boolean } = {}): Promise<number> {
  const now = opts.now ?? Date.now();
  if (backfillRunning) return 0;
  if (!opts.force && now - readStamp() < BACKFILL_INTERVAL_MS) return 0;
  // Stamp first: a failing function must not be retried on every mount
  writeStamp(now);
  backfillRunning = true;
  let total = 0;
  try {
    for (let batch = 0; batch < BACKFILL_MAX_BATCHES; batch++) {
      const res = await invokeTagSubjects({ backfill: true });
      if (!res) break;
      total += res.tagged?.length ?? 0;
      if (res.capped || !res.remaining || !res.tagged?.length) break;
    }
  } finally {
    backfillRunning = false;
  }
  return total;
}

/** Tests only. */
export function resetSubjectTaggingForTests(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  queued.clear();
  listener = null;
  backfillRunning = false;
}
